// 关卡进度的本地持久化：把当前关卡等级与关内棋盘存进 localStorage，
// 刷新页面 / 重开标签页时恢复到「这一关、这一盘」，进度不丢。纯前端、无账号，故用 localStorage 即可。
// 所有访问都包 try/catch —— 隐私模式或禁用存储时静默降级，不影响游戏可玩。

import type { Board } from "../core/types";

const LEVEL_KEY = "slide-match:level";
const BOARD_KEY = "slide-match:board";

/** 读取已保存的关卡等级；无记录或不可用时返回 1（从第一关开始）。 */
export function loadLevel(): number {
  try {
    const raw = localStorage.getItem(LEVEL_KEY);
    if (raw === null) return 1;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  } catch {
    return 1;
  }
}

/** 保存当前关卡等级；存储不可用时静默忽略。 */
export function saveLevel(level: number): void {
  try {
    localStorage.setItem(LEVEL_KEY, String(level));
  } catch {
    // localStorage 不可用（隐私模式 / 配额）——忽略即可，不影响进行中的游戏。
  }
}

/** 恢复棋盘时的校验约束：必须与当前关卡配置一致，否则视为过期存档。 */
export interface BoardShape {
  level: number;
  width: number;
  height: number;
  typeCount: number;
}

// 解析并校验存档棋盘（纯函数，便于测试）。任何不符都返回 null，由调用方重新生成：
// 关卡不符、尺寸不符、非法 Kind、某种 Kind 出现奇数次（永远消不完）、空盘。
export function parseSavedBoard(raw: string | null, shape: BoardShape): Board | null {
  if (raw === null) return null;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof data !== "object" || data === null) return null;
  const { level, board } = data as { level?: unknown; board?: unknown };
  if (level !== shape.level || !Array.isArray(board) || board.length !== shape.height) return null;

  const counts = new Map<number, number>();
  for (const row of board) {
    if (!Array.isArray(row) || row.length !== shape.width) return null;
    for (const cell of row) {
      if (cell === null) continue;
      if (!Number.isInteger(cell) || cell < 0 || cell >= shape.typeCount) return null;
      counts.set(cell, (counts.get(cell) ?? 0) + 1);
    }
  }
  if (counts.size === 0) return null;
  for (const n of counts.values()) if (n % 2 !== 0) return null;
  return board as Board;
}

/** 读取当前关卡的存档棋盘；无存档、存档过期或不可用时返回 null。 */
export function loadBoard(shape: BoardShape): Board | null {
  try {
    return parseSavedBoard(localStorage.getItem(BOARD_KEY), shape);
  } catch {
    return null;
  }
}

/** 保存关内棋盘（连同所属关卡，防止串关）；存储不可用时静默忽略。 */
export function saveBoard(level: number, board: Board): void {
  try {
    localStorage.setItem(BOARD_KEY, JSON.stringify({ level, board }));
  } catch {
    // 同 saveLevel：忽略即可。
  }
}

/** 清除关内棋盘存档（通关后调用，下一关重新生成）。 */
export function clearBoard(): void {
  try {
    localStorage.removeItem(BOARD_KEY);
  } catch {
    // 同上。
  }
}
